import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { ReactComponent as OpenIcon } from '../../assets/Icons/chevron/Chevron-Down.svg';
import { ReactComponent as CloseIcon } from '../../assets/Icons/chevron/Chevron-Up.svg';

import { Menu, MenuItem, Skeleton } from "@mui/material";

import { List, RowComponentProps } from "react-window";
import UpdatedPagination from "./UpdatedPagination";
import { GetParamsTypes } from "../../hooks/useDataParams";

export interface SelectListOption {
    id: string | number,
    name: string,
}

const ITEM_HEIGHT = 49;

function RowComponent({
    index,
    style,
    chosenOptions,
    shownOptions,
    isMultiSelect,
    handleSelect,
}: RowComponentProps<{
    chosenOptions: SelectListOption[],
    shownOptions: SelectListOption[],
    isMultiSelect: boolean,
    handleSelect: (e: ChangeEvent<HTMLInputElement>) => void,
}>) {
    const option = shownOptions[index];
        
    return (
        <label 
            style={style}
            key={option.id} 
            className={`w-full p-4 text-default hover:cursor-pointer 
            ${chosenOptions.find(opt => opt.id === option.id) ? 'bg-secondary hover:bg-secondary' : 
            'bg-none hover:bg-outline/[0.2]'} transition-all flex justify-start items-center gap-4`}
        >
            <input 
                id={`${option.id}_available`} 
                type={isMultiSelect ? 'checkbox' : 'radio'} 
                name='availableOption' 
                value={option.id}
                className='accent-primary'
                onChange={handleSelect}
                checked={chosenOptions.find(opt => opt.id === option.id) ? true : false}
            />
            <p className="text-nowrap max-w-full truncate">{option.name}</p>
        </label>
    )
}

export default function PaginatedSelectList({
    availableOptions,
    chosenOptions,
    addOption,
    removeOption,
    isMultiSelect,
    isLoading,
    isCountLoading,
    count,
    params,
    setPaginationLimit,
    setPaginationOffset,
    showSelected,
}: {
    availableOptions: SelectListOption[],
    chosenOptions: SelectListOption[],
    addOption: (id: string | number) => void,
    removeOption: (id: string | number) => void,
    isMultiSelect: boolean,
    isLoading: boolean,
    isCountLoading: boolean,
    count: number,
    params: GetParamsTypes,
    setPaginationLimit: (limit: number) => void,
    setPaginationOffset: (pageNumber: number) => void,
    showSelected: boolean,
}) {
    const [ shownOptions, setShownOptions ] = useState<SelectListOption[]>([]);
    const [ selectedGroup, setSelectedGroup ] = useState<'all' | 'selected'>('all');
    const [ sortLabel, setSortLabel ] = useState('All');
    const [ sortOptions, setSortOptions ] = useState<{ value: 'all' | 'selected', label: string }[]>([]);

    useEffect(() => {
        if (showSelected) {
            setSelectedGroup('selected');
        } else {
            setSelectedGroup('all');
        }
    }, [showSelected]);


    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const value = e.target.value;

        const option = availableOptions.find(opt => opt.id === (typeof availableOptions[0].id === 'number' ? Number(value) : value));

        if (option) {
            if (checked || !isMultiSelect) {
                addOption(option.id)
            } else {
                removeOption(option.id)
            }
        }
    }

    const optionGroups = useMemo(() => {
        const allLabel = `All (${count})`;
        const selectedLabel = `Selected (${chosenOptions.length})`;

        const groups: {
            [key: string]: {
                sortLabel: string,
                sortOptions: {value: 'all' | 'selected', label: string}[],
            }
        } = {
            all: {
                sortLabel: allLabel,
                sortOptions: [
                    { value: 'selected', label: selectedLabel },
                ],
            },
            selected: {
                sortLabel: selectedLabel,
                sortOptions: [
                    { value: 'all', label: allLabel },
                ]
            },
        };

        return groups;
    }, [
        count,
        chosenOptions.length,
    ]);

    useEffect(() => {
        const group = optionGroups[selectedGroup];
        
        let listOptions: SelectListOption[] = [];

        switch(selectedGroup) {
            case 'all':
                listOptions = availableOptions;
                break;
            case 'selected':
                listOptions = chosenOptions;
                break;
            default: 
                listOptions = availableOptions;
                break;
        }

        setShownOptions(listOptions);
        setSortLabel(group.sortLabel);
        setSortOptions(group.sortOptions);

    }, [
        optionGroups, 
        selectedGroup,
        chosenOptions,
        availableOptions,
    ]);

    const chooseSortGroup = (value: 'all' | 'selected') => {
        setSelectedGroup(value);
    };



    return (
    <>
        <div className="w-full flex flex-col justify-start items-start gap-2 grow">
            <div className="w-full flex justify-between items-center">
                <div className="flex justify-start items-center gap-3">
                    <p className="text-[14px] font-medium text-default">Available Options</p>
                    <p className="text-[12px] text-disable">{`(${chosenOptions.length} selected)`}</p>
                </div>

                <div>
                    <button onClick={handleClick} className="flex justify-end items-center gap-1">
                        <p className="text-[12px] font-medium">{sortLabel}</p>
                        <div className="w-[12px]">{open ? <CloseIcon /> : <OpenIcon />}</div>
                    </button>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        sx={{
                            marginTop: '8px',
                            '& .MuiMenu-list': {
                                padding: 0,
                            }
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: '8px'
                                }
                            }
                        }}
                    >
                        {sortOptions.length > 0 && sortOptions.map((opt, i) => (
                            <MenuItem
                                key={i}
                                sx={{
                                    fontFamily: 'Montserrat',
                                    fontWeight: 'medium',
                                    color: `#35424A`,
                                    backgroundColor: `#ffffff`,
                                    padding: '12px 20px',
                                    fontSize: '12px',
                                }}
                                onClick={() => { handleClose(); chooseSortGroup(opt.value) }}
                            >
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
            </div>

            <div className="w-full grow border border-outline rounded-lg">
                <div className="flex flex-col justify-start items-stretch min-h-[400px] w-full">
                    {isLoading ? (
                        <>
                        {Array.from(Array(6).keys()).map(key => (
                            <div key={key} className='p-4 w-full'><Skeleton variant='text' width='100%' sx={{fontSize: '1rem'}}/></div>
                        ))}
                        </>
                    ) : (
                        <>
                        {shownOptions.length > 0 && (
                            <div className='h-[400px]'>
                                <List
                                    rowComponent={RowComponent}
                                    rowCount={shownOptions.length}
                                    rowHeight={ITEM_HEIGHT}
                                    rowProps={{
                                        shownOptions,
                                        chosenOptions,
                                        isMultiSelect,
                                        handleSelect,
                                    }}
                                />
                            </div>
                        )}
                        </>
                    )}
                </div>
            </div>
            <div className="w-full flex justify-end items-center mt-2">
                <UpdatedPagination 
                    count={count}
                    limit={params.limit!}
                    offset={params.offset!}
                    setPaginationLimit={setPaginationLimit}
                    setPaginationOffset={setPaginationOffset}
                    size="sm"
                    isLoading={isCountLoading}
                />
            </div>
        </div>
        
    </>
    )
}